import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getTranslation } from '../../../utils/translations';
import userRoleService from '../../../services/userRoleService';
import { toast } from 'sonner';
import RoleFormModal from './RoleFormModal';

const UserRolesSection = ({ roles, language, onRefresh }) => {
  const t = getTranslation(language);
  const rc = t?.adminRoleCrud || {};
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editingRole, setEditingRole] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const getRoleIcon = (roleType) => {
    const icons = {
      dentist_client: 'User',
      dentist_supplier: 'UserCheck',
      distributor: 'Truck',
      administrator: 'Shield',
      production_supplier: 'Factory',
      production_responsible: 'Factory',
      logistics_coordinator: 'Package',
      operator_3d: 'Box',
      operator_thermo: 'Flame',
      operator_finition: 'Sparkles',
      operator_qualite: 'BadgeCheck',
      operators: 'Users',
      livraison: 'Truck',
    };
    return icons?.[roleType] || 'User';
  };

  const getRoleColor = (roleType) => {
    const colors = {
      dentist_client: 'bg-blue-100 text-blue-700',
      dentist_supplier: 'bg-green-100 text-green-700',
      distributor: 'bg-purple-100 text-purple-700',
      administrator: 'bg-red-100 text-red-700',
      production_supplier: 'bg-orange-100 text-orange-700',
      production_responsible: 'bg-amber-100 text-amber-800',
      logistics_coordinator: 'bg-teal-100 text-teal-800',
      operator_3d: 'bg-sky-100 text-sky-800',
      operator_thermo: 'bg-orange-100 text-orange-800',
      operator_finition: 'bg-violet-100 text-violet-800',
      operator_qualite: 'bg-emerald-100 text-emerald-800',
      operators: 'bg-slate-100 text-slate-800',
      livraison: 'bg-cyan-100 text-cyan-800',
    };
    return colors?.[roleType] || 'bg-gray-100 text-gray-700';
  };

  const openCreate = () => {
    setFormMode('create');
    setEditingRole(null);
    setFormOpen(true);
  };

  const openEdit = (role) => {
    setFormMode('edit');
    setEditingRole(role);
    setFormOpen(true);
  };

  const openDelete = async (role) => {
    try {
      const count = await userRoleService?.countRoleAssignments(role?.id);
      setPendingDelete({ role, count: typeof count === 'number' ? count : 0 });
    } catch (e) {
      console.error(e);
      toast.error(language === 'FR' ? 'Impossible de compter les affectations' : 'Cannot count assignments', {
        description: e?.message,
      });
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete?.role?.id) return;
    setDeleting(true);
    try {
      await userRoleService?.deleteRole(pendingDelete.role.id);
      toast.success(language === 'FR' ? 'Rôle supprimé' : 'Role deleted');
      setPendingDelete(null);
      await onRefresh?.();
    } catch (e) {
      console.error(e);
      toast.error(language === 'FR' ? 'Échec de suppression' : 'Delete failed', {
        description: e?.message || String(e),
      });
    } finally {
      setDeleting(false);
    }
  };

  const countLabel =
    pendingDelete?.count !== undefined &&
    typeof rc.assignmentCountLabel === 'string'
      ? rc.assignmentCountLabel.replace('{{count}}', String(pendingDelete.count))
      : '';

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-xl font-semibold text-foreground">{rc.catalogTitle}</h2>
        <Button
          type="button"
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={openCreate}
        >
          {rc.newRole}
        </Button>
      </div>

      {rc.catalogIntro && (
        <p className="text-sm text-muted-foreground max-w-4xl leading-relaxed">{rc.catalogIntro}</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles?.map((role) => (
          <div
            key={role?.id}
            className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow flex flex-col"
          >
            <div className="flex items-start gap-4 flex-1">
              <div className={`p-3 rounded-lg shrink-0 ${getRoleColor(role?.roleType)}`}>
                <Icon name={getRoleIcon(role?.roleType)} size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-mono text-muted-foreground truncate" title={role?.roleType}>
                  {role?.roleType}
                </p>
                <h3 className="font-semibold text-foreground mb-1">{role?.roleName}</h3>
                {role?.description && (
                  <p className="text-sm text-muted-foreground line-clamp-3">{role?.description}</p>
                )}
                <div className="mt-3">
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                      role?.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {role?.isActive ? t?.common?.active || t?.active : t?.common?.inactive || t?.inactive}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                size="sm"
                iconName="Pencil"
                className="flex-1"
                onClick={() => openEdit(role)}
              >
                {t?.common?.edit || rc.editRole}
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                iconName="Trash2"
                className="flex-1"
                onClick={() => openDelete(role)}
              >
                {rc.deleteRole}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {roles?.length === 0 && (
        <div className="text-center py-12 bg-muted/30 rounded-lg border border-border">
          <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{t?.common?.noData}</p>
        </div>
      )}

      <RoleFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingRole(null);
        }}
        mode={formMode}
        role={formMode === 'edit' ? editingRole : null}
        existingRoles={roles}
        language={language}
        onSaved={onRefresh}
      />

      {pendingDelete && (
        <div className="fixed inset-0 z-[66] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card border border-border rounded-lg shadow-lg max-w-md w-full p-6 space-y-4">
            <h3 className="text-lg font-semibold text-foreground">{rc.deleteTitle}</h3>
            <p className="text-sm text-muted-foreground">{rc.deleteBody}</p>
            {countLabel && <p className="text-sm font-medium text-foreground">{countLabel}</p>}
            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPendingDelete(null)}
                disabled={deleting}
              >
                {rc.cancel || t?.common?.cancel}
              </Button>
              <Button type="button" variant="destructive" onClick={confirmDelete} loading={deleting}>
                {rc.deleteRole}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRolesSection;
